import PropTypes from "prop-types";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  FormFeedback,
  Input,
  Label,
  Row
} from "reactstrap";

export const INSURANCE_REASON_OPTIONS = [
  "Missing Info",
  "Suspicious Data",
  "Docs Not Clear",
];

export const ENTRY_UPFRONT_PAYMENT_OPTIONS = [10, 25, 50];

export const CLIENT_STATUS_LABELS = {
  approved: "Approved",
  declined: "Declined",
  actionRequired: "Action Required",
  rejected: "Rejected",
  accepted: "Accepted",
};

export const CLIENT_TIER_LABELS = {
  top: "Top",
  entry: "Entry",
  lower: "Lower",
  none: "None",
};

/** Map legacy API status values to current form options. */
export function normalizeClientStatus(status) {
  const s = String(status ?? "").trim();
  if (!s) return s;
  const lower = s.toLowerCase();
  if (lower === "pendingdocs" || lower === "pending_docs") {
    return "actionRequired";
  }
  return s;
}

export function clientStatusLabel(status) {
  return CLIENT_STATUS_LABELS[status] ?? status;
}

export function clientTierLabel(tier) {
  return CLIENT_TIER_LABELS[tier] ?? tier;
}

export function initialUpFrontPaymentForTier(tier, apiValue) {
  if (tier === "top") return 0;
  if (tier === "lower") return 100;
  if (tier === "entry") {
    const n = Number(apiValue);
    return ENTRY_UPFRONT_PAYMENT_OPTIONS.includes(n) ? n : 10;
  }
  return 0;
}

export function resolveUpFrontPaymentPercentageForTier(tier, selectedValue) {
  return initialUpFrontPaymentForTier(tier, selectedValue);
}

export function applyClientTierPaymentDefaults(formik, tier) {
  if (tier !== "top") {
    formik.setFieldValue("credit", 0);
  }
  formik.setFieldValue(
    "upFrontPaymentPercentage",
    initialUpFrontPaymentForTier(tier, formik.values.upFrontPaymentPercentage)
  );
}

export function clientAccountAccessValidationShape(Yup) {
  return {
    upFrontPaymentPercentage: Yup.number().when("tier", {
      is: "entry",
      then: (schema) =>
        schema
          .typeError("Upfront payment percentage must be a number")
          .oneOf([10, 25, 50], "Must be 10, 25, or 50")
          .required("Upfront payment percentage is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    credit: Yup.number().when("tier", {
      is: "top",
      then: (schema) =>
        schema
          .typeError("Credit must be a number")
          .min(0, "Minimum is 0")
          .required("Credit is required for top tier"),
      otherwise: (schema) => schema.notRequired(),
    }),
    netPaymentTerms: Yup.number()
      .typeError("Net payment terms must be a number")
      .integer("netPaymentTerms must be an integer number")
      .min(0, "Minimum is 0")
      .required("Net payment terms is required"),
    reason: Yup.string()
      .trim()
      .max(2000, "Reason is too long")
      .when("status", {
        is: (status) => normalizeClientStatus(status) === "actionRequired",
        then: (schema) =>
          schema.required("Reason is required when action is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
  };
}

export function buildClientInsurancePayload(values) {
  const {
    isPublicLiabilityVerified,
    isEmployersLiabilityVerified,
    publicLiabilityExpiryDate,
    employersLiabilityExpiryDate,
    publicLiabilityReason,
    employersLiabilityReason,
  } = values;

  const obj = {
    isPublicLiabilityVerified: !!isPublicLiabilityVerified,
    isEmployersLiabilityVerified: !!isEmployersLiabilityVerified,
  };

  if (publicLiabilityReason) obj.publicLiabilityReason = publicLiabilityReason;
  if (isPublicLiabilityVerified && publicLiabilityExpiryDate) {
    obj.publicLiabilityExpiryDate = publicLiabilityExpiryDate;
  }
  if (employersLiabilityReason) obj.employersLiabilityReason = employersLiabilityReason;
  if (isEmployersLiabilityVerified && employersLiabilityExpiryDate) {
    obj.employersLiabilityExpiryDate = employersLiabilityExpiryDate;
  }

  return obj;
}

export function clientInsuranceValidationShape(Yup) {
  return {
    isPublicLiabilityVerified: Yup.boolean().required("Required"),
    isEmployersLiabilityVerified: Yup.boolean().required("Required"),
    publicLiabilityExpiryDate: Yup.string().when("isPublicLiabilityVerified", {
      is: true,
      then: (s) =>
        s.required("Public liability expiry date is required when verified"),
      otherwise: (s) => s.notRequired(),
    }),
    employersLiabilityExpiryDate: Yup.string().when(
      "isEmployersLiabilityVerified",
      {
        is: true,
        then: (s) =>
          s.required(
            "Employers liability expiry date is required when verified"
          ),
        otherwise: (s) => s.notRequired(),
      }
    ),
    publicLiabilityReason: Yup.string().when("isPublicLiabilityVerified", {
      is: false,
      then: (schema) =>
        schema
          .oneOf(INSURANCE_REASON_OPTIONS, "Invalid reason")
          .required("Public liability reason is required when not verified"),
      otherwise: (schema) => schema.notRequired(),
    }),
    employersLiabilityReason: Yup.string().when("isEmployersLiabilityVerified", {
      is: false,
      then: (schema) =>
        schema
          .oneOf(INSURANCE_REASON_OPTIONS, "Invalid reason")
          .required(
            "Employers liability reason is required when not verified"
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  };
}

export function AdminActionCard({
  variant,
  title,
  subtitle,
  isOpen,
  onToggle,
  children,
}) {
  const iconClass =
    variant === "create"
      ? "mdi-account-plus-outline"
      : "mdi-account-edit-outline";
  const iconModifier =
    variant === "create"
      ? "worker-admin-action-card__icon--create"
      : "worker-admin-action-card__icon--update";

  return (
    <Card className="mt-4 worker-admin-action-card border shadow-sm">
      <CardHeader
        onClick={onToggle}
        className="worker-admin-action-card__header d-flex justify-content-between align-items-center"
      >
        <div className="d-flex align-items-center gap-3">
          <span
            className={`worker-admin-action-card__icon ${iconModifier}`}
            aria-hidden
          >
            <i className={`mdi ${iconClass}`} />
          </span>
          <div>
            <h5 className="mb-0">{title}</h5>
            {subtitle ? (
              <p className="text-muted small mb-0 mt-1">{subtitle}</p>
            ) : null}
          </div>
        </div>
        <i
          className={`mdi mdi-chevron-${
            isOpen ? "up" : "down"
          } worker-admin-action-card__chevron bg-dark text-white rounded-circle`}
          aria-hidden
        />
      </CardHeader>
      <Collapse isOpen={isOpen}>
        <CardBody className="worker-admin-action-card__body">{children}</CardBody>
      </Collapse>
    </Card>
  );
}

AdminActionCard.propTypes = {
  variant: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export function AdminFormSection({ title, description, icon, children }) {
  return (
    <div className="worker-admin-form-section">
      <div className="worker-admin-form-section__head">
        {icon ? (
          <span className="worker-admin-form-section__icon" aria-hidden>
            <i className={`mdi ${icon}`} />
          </span>
        ) : null}
        <div>
          <h6 className="worker-admin-form-section__title">{title}</h6>
          {description ? (
            <p className="text-muted small mb-0 mt-1">{description}</p>
          ) : null}
        </div>
      </div>
      <Row className="gy-3">{children}</Row>
    </div>
  );
}

AdminFormSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  icon: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export function AdminFormFooter({ hint, children }) {
  return (
    <div className="worker-admin-form-footer">
      {hint ? <p className="worker-admin-form-footer__hint">{hint}</p> : <span />}
      <div className="d-flex flex-column align-items-end">{children}</div>
    </div>
  );
}

AdminFormFooter.propTypes = {
  hint: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export function AdminInsuranceVerificationFields({
  formik,
  safeText,
  isVerified,
  onVerifiedChange,
  idSuffix = "",
}) {
  const employersVerified = isVerified("isEmployersLiabilityVerified");
  const publicVerified = isVerified("isPublicLiabilityVerified");

  return (
    <>
      <Col md={12}>
        <div className="worker-admin-rtw-group">
          <p className="worker-admin-rtw-group__label">Employers liability</p>
          <Row className="gy-3">
            <Col md={6}>
              <Label
                className="form-label"
                htmlFor={`isEmployersLiabilityVerified${idSuffix}`}
              >
                Verified?
              </Label>
              <Input
                id={`isEmployersLiabilityVerified${idSuffix}`}
                name="isEmployersLiabilityVerified"
                type="select"
                value={employersVerified ? "true" : "false"}
                onChange={(e) =>
                  onVerifiedChange(
                    "isEmployersLiabilityVerified",
                    e.target.value === "true"
                  )
                }
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </Input>
            </Col>
            {!employersVerified && (
              <Col md={6}>
                <Label
                  className="form-label"
                  htmlFor={`employersLiabilityReason${idSuffix}`}
                >
                  Reason
                </Label>
                <Input
                  id={`employersLiabilityReason${idSuffix}`}
                  name="employersLiabilityReason"
                  type="select"
                  value={formik.values.employersLiabilityReason || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.employersLiabilityReason &&
                    !!formik.errors.employersLiabilityReason
                  }
                >
                  <option value="">Select reason</option>
                  {INSURANCE_REASON_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Input>
                <FormFeedback>
                  {safeText(formik.errors.employersLiabilityReason)}
                </FormFeedback>
              </Col>
            )}
            {employersVerified && (
              <Col md={6}>
                <Label
                  className="form-label"
                  htmlFor={`employersLiabilityExpiryDate${idSuffix}`}
                >
                  Expiry date
                </Label>
                <Input
                  id={`employersLiabilityExpiryDate${idSuffix}`}
                  name="employersLiabilityExpiryDate"
                  type="date"
                  value={formik.values.employersLiabilityExpiryDate || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.employersLiabilityExpiryDate &&
                    !!formik.errors.employersLiabilityExpiryDate
                  }
                />
                <FormFeedback>
                  {safeText(formik.errors.employersLiabilityExpiryDate)}
                </FormFeedback>
              </Col>
            )}
          </Row>
        </div>
      </Col>

      <Col md={12}>
        <div className="worker-admin-rtw-group">
          <p className="worker-admin-rtw-group__label">Public liability</p>
          <Row className="gy-3">
            <Col md={6}>
              <Label
                className="form-label"
                htmlFor={`isPublicLiabilityVerified${idSuffix}`}
              >
                Verified?
              </Label>
              <Input
                id={`isPublicLiabilityVerified${idSuffix}`}
                name="isPublicLiabilityVerified"
                type="select"
                value={publicVerified ? "true" : "false"}
                onChange={(e) =>
                  onVerifiedChange(
                    "isPublicLiabilityVerified",
                    e.target.value === "true"
                  )
                }
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </Input>
            </Col>
            {!publicVerified && (
              <Col md={6}>
                <Label
                  className="form-label"
                  htmlFor={`publicLiabilityReason${idSuffix}`}
                >
                  Reason
                </Label>
                <Input
                  id={`publicLiabilityReason${idSuffix}`}
                  name="publicLiabilityReason"
                  type="select"
                  value={formik.values.publicLiabilityReason || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.publicLiabilityReason &&
                    !!formik.errors.publicLiabilityReason
                  }
                >
                  <option value="">Select reason</option>
                  {INSURANCE_REASON_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Input>
                <FormFeedback>
                  {safeText(formik.errors.publicLiabilityReason)}
                </FormFeedback>
              </Col>
            )}
            {publicVerified && (
              <Col md={6}>
                <Label
                  className="form-label"
                  htmlFor={`publicLiabilityExpiryDate${idSuffix}`}
                >
                  Expiry date
                </Label>
                <Input
                  id={`publicLiabilityExpiryDate${idSuffix}`}
                  name="publicLiabilityExpiryDate"
                  type="date"
                  value={formik.values.publicLiabilityExpiryDate || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.publicLiabilityExpiryDate &&
                    !!formik.errors.publicLiabilityExpiryDate
                  }
                />
                <FormFeedback>
                  {safeText(formik.errors.publicLiabilityExpiryDate)}
                </FormFeedback>
              </Col>
            )}
          </Row>
        </div>
      </Col>
    </>
  );
}

AdminInsuranceVerificationFields.propTypes = {
  formik: PropTypes.object.isRequired,
  safeText: PropTypes.func.isRequired,
  isVerified: PropTypes.func.isRequired,
  onVerifiedChange: PropTypes.func.isRequired,
  idSuffix: PropTypes.string,
};

export function ClientInsuranceReadOnlyPanel({
  ins,
  normalizeToArray,
  ListLinks,
  BoolIcon,
  formatDisplayDate,
}) {
  const employersFiles = normalizeToArray(ins?.employersLiability);
  const publicFiles = normalizeToArray(ins?.publicLiability);

  return (
    <Row className="gy-3">
      <Col md={12}>
        <div className="d-flex flex-wrap gap-3 align-items-center">
          <div>
            <small className="text-muted d-block">Info accurate (onboarding)</small>
            <BoolIcon value={ins?.infoAccurate} />
          </div>
        </div>
      </Col>

      <Col md={6}>
        <div className="worker-admin-rtw-group h-100">
          <p className="worker-admin-rtw-group__label">Employers liability</p>
          <Row className="gy-2">
            <Col xs={12}>
              <small className="text-muted d-block">Policy files</small>
              {ListLinks(employersFiles)}
            </Col>
            <Col xs={6}>
              <small className="text-muted d-block">Verified</small>
              <BoolIcon value={ins?.isEmployersLiabilityVerified} />
            </Col>
            <Col xs={6}>
              <small className="text-muted d-block">Expiry</small>
              <div>{formatDisplayDate(ins?.employersLiabilityExpiryDate)}</div>
            </Col>
            {!ins?.isEmployersLiabilityVerified && ins?.employersLiabilityReason && (
              <Col xs={12}>
                <small className="text-muted d-block">Reason</small>
                <div>{ins.employersLiabilityReason}</div>
              </Col>
            )}
          </Row>
        </div>
      </Col>

      <Col md={6}>
        <div className="worker-admin-rtw-group h-100">
          <p className="worker-admin-rtw-group__label">Public liability</p>
          <Row className="gy-2">
            <Col xs={12}>
              <small className="text-muted d-block">Policy files</small>
              {ListLinks(publicFiles)}
            </Col>
            <Col xs={6}>
              <small className="text-muted d-block">Verified</small>
              <BoolIcon value={ins?.isPublicLiabilityVerified} />
            </Col>
            <Col xs={6}>
              <small className="text-muted d-block">Expiry</small>
              <div>{formatDisplayDate(ins?.publicLiabilityExpiryDate)}</div>
            </Col>
            {!ins?.isPublicLiabilityVerified && ins?.publicLiabilityReason && (
              <Col xs={12}>
                <small className="text-muted d-block">Reason</small>
                <div>{ins.publicLiabilityReason}</div>
              </Col>
            )}
          </Row>
        </div>
      </Col>
    </Row>
  );
}

ClientInsuranceReadOnlyPanel.propTypes = {
  ins: PropTypes.object.isRequired,
  normalizeToArray: PropTypes.func.isRequired,
  ListLinks: PropTypes.func.isRequired,
  BoolIcon: PropTypes.func.isRequired,
  formatDisplayDate: PropTypes.func.isRequired,
};

export function AdminClientApprovalFormSections({
  variant,
  formik,
  safeText,
  statusOptions,
  tierOptions,
  ocdStatusOptions,
  isVerified,
  onInsuranceVerifiedChange,
}) {
  const isCreate = variant === "create";
  const idSuffix = isCreate ? "" : "_u";

  return (
    <div className="worker-admin-action-card__form-surface">
      <AdminFormSection
        title="Insurance verification"
        description="Review uploaded policies and record verification status, reasons, and expiry dates."
        icon="mdi-shield-check-outline"
      >
        <AdminInsuranceVerificationFields
          formik={formik}
          safeText={safeText}
          isVerified={isVerified}
          onVerifiedChange={onInsuranceVerifiedChange}
          idSuffix={idSuffix}
        />
      </AdminFormSection>

      <AdminFormSection
        title="Ownership & control"
        description="Set clearance status for ownership and control declaration."
        icon="mdi-account-group-outline"
      >
        <Col md={6}>
          <Label className="form-label" htmlFor={`ocdStatus${idSuffix}`}>
            Status
          </Label>
          <Input
            id={`ocdStatus${idSuffix}`}
            name="ocdStatus"
            type="select"
            value={formik.values.ocdStatus}
            onChange={(e) => {
              formik.handleChange(e);
              if (e.target.value !== "flagged") {
                formik.setFieldValue("ocdReason", "");
              }
            }}
            onBlur={formik.handleBlur}
            invalid={formik.touched.ocdStatus && !!formik.errors.ocdStatus}
          >
            {ocdStatusOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Input>
          <FormFeedback>{safeText(formik.errors.ocdStatus)}</FormFeedback>
        </Col>
        <Col md={6}>
          <Label className="form-label" htmlFor={`ocdReason${idSuffix}`}>
            Reason (required when flagged)
          </Label>
          {formik.values.ocdStatus === "cleared" ? (
            <div className="form-control-plaintext text-muted">Not required</div>
          ) : (
            <>
              <Input
                id={`ocdReason${idSuffix}`}
                name="ocdReason"
                type="select"
                value={formik.values.ocdReason || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched.ocdReason && !!formik.errors.ocdReason}
              >
                <option value="">Select reason</option>
                {INSURANCE_REASON_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Input>
              <FormFeedback>{safeText(formik.errors.ocdReason)}</FormFeedback>
            </>
          )}
        </Col>
      </AdminFormSection>

      <AdminFormSection
        title="Account & access"
        description={
          isCreate
            ? "Approve the client account and configure tier and billing settings."
            : "Update approval status, tier, and billing settings."
        }
        icon="mdi-shield-account-outline"
      >
        <Col md={12}>
          <Label className="form-label" htmlFor={`email${idSuffix}`}>
            Email
          </Label>
          <Input
            id={`email${idSuffix}`}
            name="email"
            type="email"
            value={formik.values.email}
            disabled
            readOnly
          />
        </Col>

        <Col md={3}>
          <Label className="form-label" htmlFor={`status${idSuffix}-abc`}>
            Status
          </Label>
          <Input
            id={`status${idSuffix}-abc`}
            name="status"
            type="select"
            value={formik.values.status}
            onChange={(e) => {
              formik.handleChange(e);
              if (normalizeClientStatus(e.target.value) !== "actionRequired") {
                formik.setFieldValue("reason", "");
              }
            }}
            onBlur={formik.handleBlur}
            invalid={formik.touched.status && !!formik.errors.status}
          >
            {statusOptions.map((t) => (
              <option key={t} value={t}>
                {clientStatusLabel(t)}
              </option>
            ))}
          </Input>
          <FormFeedback>{safeText(formik.errors.status)}</FormFeedback>
        </Col>

        <Col md={3}>
          <Label className="form-label" htmlFor={`tier${idSuffix}`}>
            Tier
          </Label>
          <Input
            id={`tier${idSuffix}`}
            name="tier"
            type="select"
            value={formik.values.tier}
            onChange={(e) => {
              formik.handleChange(e);
              applyClientTierPaymentDefaults(formik, e.target.value);
            }}
            onBlur={formik.handleBlur}
            invalid={formik.touched.tier && !!formik.errors.tier}
          >
            {tierOptions.map((t) => (
              <option key={t} value={t}>
                {clientTierLabel(t)}
              </option>
            ))}
          </Input>
          <FormFeedback>{safeText(formik.errors.tier)}</FormFeedback>
        </Col>

        {normalizeClientStatus(formik.values.status) === "actionRequired" && (
          <Col md={3}>
            <Label className="form-label" htmlFor={`reason${idSuffix}`}>
              Reason
            </Label>
            <Input
              id={`reason${idSuffix}`}
              name="reason"
              type="textarea"
              rows={3}
              value={formik.values.reason || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.reason && !!formik.errors.reason}
              placeholder="Why action is required"
            />
            <FormFeedback>{safeText(formik.errors.reason)}</FormFeedback>
          </Col>
        )}

        {formik.values.tier === "entry" && (
          <Col md={3}>
            <Label
              className="form-label"
              htmlFor={`upFrontPaymentPercentage${idSuffix}`}
            >
              Upfront payment %
            </Label>
            <Input
              id={`upFrontPaymentPercentage${idSuffix}`}
              name="upFrontPaymentPercentage"
              type="select"
              value={formik.values.upFrontPaymentPercentage}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={
                formik.touched.upFrontPaymentPercentage &&
                !!formik.errors.upFrontPaymentPercentage
              }
            >
              {ENTRY_UPFRONT_PAYMENT_OPTIONS.map((percentage) => (
                <option key={percentage} value={percentage}>
                  {percentage}%
                </option>
              ))}
            </Input>
            <FormFeedback>
              {safeText(formik.errors.upFrontPaymentPercentage)}
            </FormFeedback>
          </Col>
        )}
        <Col md={3}>
          <Label className="form-label" htmlFor={`netPaymentTerms${idSuffix}`}>
            Net Payment Terms (Days)
          </Label>
          <Input
            id={`netPaymentTerms${idSuffix}`}
            name="netPaymentTerms"
            type="number"
            min="0"
            step="1"
            value={formik.values.netPaymentTerms}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              formik.touched.netPaymentTerms && !!formik.errors.netPaymentTerms
            }
          />
          <FormFeedback>
            {safeText(formik.errors.netPaymentTerms)}
          </FormFeedback>
        </Col>
        {formik.values.tier === "top" && (
          <Col md={3}>
            <Label className="form-label" htmlFor={`credit${idSuffix}`}>
              Credit
            </Label>
            <Input
              id={`credit${idSuffix}`}
              name="credit"
              type="number"
              min="0"
              step="1"
              value={formik.values.credit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.credit && !!formik.errors.credit}
            />
            <FormFeedback>{safeText(formik.errors.credit)}</FormFeedback>
          </Col>
        )}
      </AdminFormSection>
    </div>
  );
}

AdminClientApprovalFormSections.propTypes = {
  variant: PropTypes.string.isRequired,
  formik: PropTypes.object.isRequired,
  safeText: PropTypes.func.isRequired,
  statusOptions: PropTypes.array.isRequired,
  tierOptions: PropTypes.array.isRequired,
  ocdStatusOptions: PropTypes.array.isRequired,
  isVerified: PropTypes.func.isRequired,
  onInsuranceVerifiedChange: PropTypes.func.isRequired,
};